import { useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

export default function DemoRequestModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    companyType: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTypeChange = (value) => {
    setFormData({ ...formData, companyType: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.companyName || !formData.companyType) {
      toast.error("Please fill in all required fields.", { duration: 3000 });
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error("Please enter a valid email address.", { duration: 3000 });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'modal' })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast.success("Thank you! Our team will contact you within 24 hours to schedule your demo.", {
          duration: 5000,
        });
        setFormData({ name: "", email: "", phone: "", companyName: "", companyType: "" });
        onClose();
      } else {
        toast.error(result.message || "Something went wrong. Please try again.", { duration: 4000 });
      }
    } catch (error) {
      toast.error("Network error. Please try again.", { duration: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
            data-testid="demo-modal"
          >
            <div className="p-6 sm:p-8">
              {/* Header */}
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h2 
                    className="text-xl font-medium text-[#1A362D]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Request a Demo
                  </h2>
                  <p className="text-sm text-[#5C6C65] mt-1">
                    Fill out the form and we'll be in touch shortly
                  </p>
                </div>
                <button 
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-[#F9F6F0] flex items-center justify-center hover:bg-[#E2E8D8] transition-colors flex-shrink-0 ml-4"
                  data-testid="demo-modal-close"
                >
                  <X size={16} className="text-[#5C6C65]" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-[#1A362D] font-medium text-sm">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="mt-1.5 rounded-lg border-[#E2E8D8] focus:border-[#1A362D]"
                    data-testid="demo-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#1A362D] font-medium text-sm">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@practice.com"
                    className="mt-1.5 rounded-lg border-[#E2E8D8] focus:border-[#1A362D]"
                    data-testid="demo-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#1A362D] font-medium text-sm">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="mt-1.5 rounded-lg border-[#E2E8D8] focus:border-[#1A362D]"
                    data-testid="demo-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="companyName" className="text-[#1A362D] font-medium text-sm">
                    Practice / Company Name *
                  </Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="ABC Medical Group"
                    className="mt-1.5 rounded-lg border-[#E2E8D8] focus:border-[#1A362D]"
                    data-testid="demo-company"
                  />
                </div>

                <div>
                  <Label className="text-[#1A362D] font-medium text-sm">
                    Company Type *
                  </Label>
                  <Select onValueChange={handleTypeChange} value={formData.companyType}>
                    <SelectTrigger 
                      className="mt-1.5 rounded-lg border-[#E2E8D8] focus:border-[#1A362D]"
                      data-testid="demo-type"
                    >
                      <SelectValue placeholder="Select your company type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="provider">Healthcare Provider / Practice</SelectItem>
                      <SelectItem value="billing">Billing Company</SelectItem>
                      <SelectItem value="credentialing">Credentialing Company</SelectItem>
                      <SelectItem value="hospital">Hospital / Health System</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-pill btn-accent py-5 mt-2"
                  disabled={isSubmitting}
                  data-testid="demo-submit"
                >
                  {isSubmitting ? "Submitting..." : "Request Demo"}
                </Button>

                <p className="text-xs text-center text-[#5C6C65]">
                  By submitting, you agree to our Privacy Policy and Terms of Service.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
